/**
 * <LeadForm schema={…} context={…} /> — renders any lead form from a schema
 * (see src/forms/definitions/) and submits it to POST /api/v2/leads.
 *
 * Schema shape (all optional unless noted):
 *   id*, label*            form identity (Website_Form_ID / _Label in Zoho)
 *   crm                    { layout, leadSource, tags }
 *   fields | steps*        fields: [field | [field, field] (a row)]
 *                          steps:  [{ title, fields }]
 *   consent                { variant: standard|condensed|minimal|notice|referral|none, marketing:false }
 *                          (a page can override the variant with the consentVariant prop)
 *   submitLabel, success   { title, body, allowReset }
 *   contextToCrm(ctx, v)   derived CRM fields from page/tool state
 *   summary(v, ctx)        one-line Interested_In
 *   footer                 node rendered under the submit button
 */
import { useEffect, useId, useState } from "react";
import { AlertCircle, ArrowLeft, ArrowRight, Send } from "lucide-react";
import ConsentBlock from "@/components/ConsentBlock";
import Honeypot from "@/components/Honeypot";
import { LottieSuccess } from "@/components/LottieAnimations";
import { Field } from "./fields";
import useLeadForm from "./useLeadForm";
import { isEmpty, isVisible } from "./validation";

function Success({ success = {}, onReset, compact }) {
  return (
    <div className={`text-center ${compact ? "py-6" : "py-12"} space-y-4`} data-testid="lead-form-success">
      <LottieSuccess size={compact ? 64 : 80} className="mx-auto" />
      <h3 className="font-heading text-2xl font-bold text-brand-black">{success.title || "Thank you!"}</h3>
      <p className="text-brand-grey">
        {success.body || "We've received your details and will be in touch shortly."}
      </p>
      {success.allowReset && (
        <button
          type="button"
          onClick={onReset}
          className="text-brand-aqua hover:underline text-sm font-semibold"
          data-testid="lead-form-reset"
        >
          {success.resetLabel || "Send another message"}
        </button>
      )}
    </div>
  );
}

export default function LeadForm({
  schema,
  context,
  defaults,
  onSuccess,
  revealed = true,
  variant = "default",
  header = null,
  renderSuccess,
  className = "",
  consentVariant,
}) {
  const ctx = context || {};
  const form = useLeadForm(schema, { context: ctx, defaults, onSuccess });
  const { values, setValue, errors, status, submitError, step, steps, next, back, submit, reset } = form;
  const [fileNotices, setFileNotices] = useState({});
  const idPrefix = `lf-${schema.id}-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const compact = variant === "compact";
  const { resetClock } = form;

  // Popups/drawers mount early but become visible later: count
  // time-to-submit from when the visitor could actually see the form.
  useEffect(() => {
    if (revealed) resetClock();
  }, [revealed, resetClock]);

  if (status === "success") {
    return renderSuccess ? renderSuccess({ reset, values }) : (
      <Success success={schema.success} onReset={reset} compact={compact} />
    );
  }

  const current = steps[step];
  const isLast = step === steps.length - 1;
  // A page can swap only the wording of the consent block (e.g. the Merchant
  // Cash Advance page's funding-introduction notice) without a new schema, so
  // the form keeps its id, fields and CRM mapping.
  const consent = { ...(schema.consent || { variant: "standard" }), ...(consentVariant ? { variant: consentVariant } : {}) };
  const requiredFilled = steps
    .flatMap((s) => s.fields.flatMap((e) => (Array.isArray(e) ? e : [e])))
    .filter((f) => f.required && isVisible(f, values, ctx))
    .every((f) => !isEmpty(values[f.name]));

  const renderField = (field) => {
    if (!isVisible(field, values, ctx)) return null;
    const id = `${idPrefix}-${field.name}`;
    return (
      <Field
        key={field.name}
        field={field}
        id={id}
        value={values[field.name]}
        error={errors[field.name] || fileNotices[field.name]}
        onChange={(v) => setValue(field.name, v)}
        onReject={(msg) => setFileNotices((prev) => ({ ...prev, [field.name]: msg }))}
        values={values}
        context={ctx}
      />
    );
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!isLast) next();
    else submit();
  };

  const submitting = status === "submitting";
  const submitText = submitting
    ? form.uploadProgress !== null && form.uploadProgress < 1
      ? `Uploading… ${Math.round(form.uploadProgress * 100)}%`
      : "Sending…"
    : schema.submitLabel || "Send my details";

  // method="post": if hydration fails and the browser submits natively, values
  // go in a request body rather than the address bar.
  return (
    <form
      onSubmit={onSubmit}
      method="post"
      noValidate
      className={`${compact ? "space-y-3" : "space-y-4"} ${className}`}
      data-testid={`lead-form-${schema.id}`}
    >
      {header}
      {steps.length > 1 && (
        <div className="flex items-center justify-between text-xs text-brand-grey" data-testid="lead-form-step">
          <span className="font-semibold text-brand-black">{current.title || ""}</span>
          <span>{`Step ${step + 1} of ${steps.length}`}</span>
        </div>
      )}
      {submitError && (
        <div
          className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm"
          role="alert"
          data-testid="lead-form-error"
        >
          <AlertCircle size={16} className="shrink-0" />
          <span>{submitError}</span>
        </div>
      )}
      {current.intro && <p className="text-sm text-brand-grey">{current.intro}</p>}
      {current.fields.map((entry) =>
        Array.isArray(entry) ? (
          <div key={entry.map((f) => f.name).join("+")} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {entry.map(renderField)}
          </div>
        ) : (
          renderField(entry)
        )
      )}
      <Honeypot value={form.hp} onChange={(e) => form.setHp(e.target.value)} />
      <div className="flex gap-3">
        {step > 0 && (
          <button
            type="button"
            onClick={back}
            className="rounded-full border border-brand-light-grey px-5 py-3.5 text-sm font-semibold text-brand-black hover:border-brand-aqua flex items-center gap-2"
            data-testid="lead-form-back"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className={`flex-1 btn-form-ready ${requiredFilled || !isLast ? "is-ready" : ""} text-white font-semibold rounded-full px-6 py-3.5 text-sm flex items-center justify-center gap-2 disabled:opacity-60`}
          data-testid="lead-form-submit"
        >
          {isLast ? !submitting && <Send size={16} /> : null}
          <span>{isLast ? submitText : "Continue"}</span>
          {!isLast && <ArrowRight size={16} />}
        </button>
      </div>
      {isLast && schema.footer}
      {isLast && consent.variant !== "none" && (
        <ConsentBlock
          variant={consent.variant || "standard"}
          marketingConsent={form.marketingConsent}
          onMarketingConsentChange={form.setMarketingConsent}
          testIdPrefix={`lead-form-${schema.id}`}
        />
      )}
    </form>
  );
}
